<template>
  <div>
    <v-container fluid>
      <!-- Header -->
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar dark class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">Loan Interest Rate</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2" style="margin-top: 1rem">
        <v-container class="px-6 py-6">
          <!-- Filters -->
          <v-row class="mb-4">
            <!-- Title Section -->
            <v-col cols="12" sm="12" md="2" lg="2" xl="3" class="d-flex align-center pb-3 pb-md-0">
              <div>
                <h3 class="text-h6 mb-1">Loan Interest Rate list </h3>
              </div>
            </v-col>
            <v-col cols="12" sm="12" md="3" lg="3" xl="3">
              <v-text-field v-model="search" label="Search" prepend-inner-icon="mdi-magnify" variant="outlined" rounded class="search-field" clearable hide-details />
            </v-col>
            <v-col cols="12" sm="12" md="2" lg="3" xl="3">
              <v-select 
                v-model="categoryFilter" 
                label="Category" 
                :items="categoriesData" 
                item-title="categoryName"
                item-value="categoryID"
                variant="outlined" 
                rounded 
                class="search-field" 
                clearable 
                hide-details 
              />
            </v-col>
            <v-col cols="12" sm="12" md="2" lg="2" xl="3" class="d-flex justify-end align-center">
              <div class="d-flex flex-column flex-sm-row gap-2 w-100">
                <v-btn v-if="permissions.edit" class="btn-category flex-grow-1 flex-sm-grow-0" @click="openCategoriesDialog()">
                  <v-icon start>mdi-shape</v-icon>
                  Categories
                </v-btn>
                <v-btn v-if="permissions.add" class="btn-layout flex-grow-1 flex-sm-grow-0" @click="openDialog()">
                  <v-icon start>mdi-plus</v-icon>
                  Add
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <!-- Data Table -->
          <v-data-table :headers="headers" :items="filteredItems" :loading="loading" class="elevation-1 my-data-table modern-table" density="comfortable" :items-per-page="10">
            <template v-slot:item.categoryID="{ item }">
              <v-chip :color="item.categoryID ? getCategoryColorById(item.categoryID) : 'grey'" size="small" variant="outlined">
                {{ item.categoryID ? getCategoryNameById(item.categoryID) : 'No Category' }}
              </v-chip>
            </template>

            <template v-slot:item.effectiveStartDate="{ item }">
              {{ formatDateDisplay(item.effectiveStartDate) }}
            </template>

            <template v-slot:item.effectiveEndDate="{ item }">
              {{ formatDateDisplay(item.effectiveEndDate) }}
            </template>

            <template v-slot:item.isActive="{ item }">
             <td style="text-align: center">
                <v-switch
                  :model-value="item.isActive !== false"
                  @update:model-value="toggleActive(item, $event)"
                  size="small"
                  density="compact"
                  inset
                  hide-details
                  :disabled="!permissions.edit"
                  class="toggle-switch"
                  :class="item.isActive ? 'on' : 'off'"
                ></v-switch>
              </td>
            </template>

            <template v-slot:item.duplicate="{ item }">
              <div style="display: flex; align-items: center; justify-content: center;">
                <v-btn
                  v-if="permissions.add"
                  size="small"
                  variant="flat"
                  class="btn-duplicate"
                  @click="popupDuplicate(item)"
                  elevation="1"
                >
                  <v-icon size="16" class="mr-0">mdi-content-copy</v-icon>
                  <v-tooltip activator="parent" location="top">Create a duplicate of this interest rate</v-tooltip>
                </v-btn>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="action-buttons-group">
                <v-btn size="small" variant="outlined" color="primary" @click="openDialog(item)" class="action-btn">
                  <v-icon v-if="!permissions.edit" size="16" color="primary">mdi-eye-outline</v-icon>
                  <v-img v-else :src="images.EditImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">{{ permissions.edit ? 'Edit' : 'View' }}</v-tooltip>
                </v-btn>
                <v-btn v-if="permissions.delete" size="small" variant="outlined" color="error" @click="confirmDelete(item)" class="action-btn">
                  <v-img :src="images.DeleteImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-container>
      </v-card>
    </v-container>

    <!-- Loading Please Wait... -->
    <v-overlay :model-value="loadingDialog" class="align-center justify-center" persistent>
      <v-card class="loading-card" elevation="8">
        <v-card-text class="text-center">
          <v-progress-circular
            indeterminate
            size="64"
            color="primary"
            class="mb-4"
          ></v-progress-circular>
          <h3 class="loading-title">Loading...</h3>
          <p class="loading-subtitle">Please wait</p>
        </v-card-text>
      </v-card>
    </v-overlay>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="1400px">
      <v-card class="add-proactive-dialog">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center">
            <div class="header-icon-container">
              <v-avatar class="icon-background" size="46">
                <v-icon color="#4b2667" size="24">
                  {{ editMode ? 'mdi-pencil' : 'mdi-plus' }}
                </v-icon>
              </v-avatar>
            </div>
            <div>
              <div class="dialog-title">
                {{ editMode ? (permissions.edit ? 'Edit Interest Rate' : 'View Interest Rate') : 'Add New Interest Rate' }}
              </div>
              <div class="dialog-subtitle">
                {{ editMode ? 'Update an interest rate' : 'Create a new interest rate' }}
              </div>
            </div>
          </div>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="formValid">
            <!-- Top Section with Light Gray Background -->
            <div style="background-color: #eeeff3; padding: 20px; border-radius: 8px; margin-bottom: 24px;">
              <!-- Interest Rate Name -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2" style="color: #333; font-weight: 500;">Interest Rate Name</div>
                <v-text-field 
                  v-model="formData.interestRateName" 
                  placeholder="Interest Rate Name"
                  variant="outlined" 
                  density="comfortable" 
                  :rules="[rules.required]" 
                  hide-details="auto"
                  bg-color="white"
                  class="rounded-input"
                />
              </div>

              <!-- Type and Category Row -->
              <v-row class="mb-0">
                <v-col cols="6" class="pb-0">
                  <div class="text-subtitle-2 mb-2" style="color: #333; font-weight: 500;">Type</div>
                  <v-combobox
                    ref="interestRateTypeField"
                    v-model="formData.interestRateType"
                    :items="interestRateTypes"
                    variant="outlined"
                    density="comfortable"
                    :rules="[rules.required]"
                    hide-details="auto"
                    bg-color="white"
                    chips
                    closable-chips
                    class="rounded-input"
                    @update:model-value="onInterestRateTypeChangeAndValidate"
                  />
                </v-col>
                <v-col cols="6" class="pb-0">
                  <div class="text-subtitle-2 mb-2" style="color: #333; font-weight: 500;">Category</div>
                  <v-select
                    v-model="formData.categoryID"
                    :items="categoriesData"
                    item-title="categoryName"
                    item-value="categoryID"
                    placeholder="Select Category"
                    variant="outlined"
                    density="comfortable"
                    :rules="[rules.required]"
                    hide-details="auto"
                    :loading="loadingCategories"
                    bg-color="white"
                    class="rounded-input"
                  />
                </v-col>
              </v-row>
            </div>

            <!-- Effective Dates Row -->
            <v-row class="mb-4">
              <v-col cols="6" class="pb-0">
                <div class="text-subtitle-2 mb-2" style="color: #333; font-weight: 500;">Effective Start Date</div>
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ props }">
                    <v-text-field
                      :model-value="formatDateDisplay(formData.effectiveStartDate)"
                      placeholder="Select"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.effectiveDateRequired]"
                      hide-details="auto"
                      bg-color="white"
                      readonly
                      class="rounded-input"
                      v-bind="props"
                    >
                      <template v-slot:append-inner>
                        <v-icon color="primary">mdi-calendar</v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="formData.effectiveStartDate"
                    @update:model-value="onStartDateChange"
                    :max="startDateMax"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="6" class="pb-0">
                <div class="text-subtitle-2 mb-2" style="color: #333; font-weight: 500;">Effective End Date</div>
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ props }">
                    <v-text-field
                      :model-value="formatDateDisplay(formData.effectiveEndDate)"
                      placeholder="Select"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                      bg-color="white"
                      readonly
                      clearable
                      @click:clear="formData.effectiveEndDate = null"
                      class="rounded-input"
                      v-bind="props"
                    >
                      <template v-slot:append-inner>
                        <v-icon color="primary">mdi-calendar</v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="formData.effectiveEndDate"
                    @update:model-value="onEndDateChange"
                    :min="endDateMin"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <!-- Enable Toggle -->
            <div class="mb-4">
              <v-switch
                  v-model="formData.isActive"
                  size="small"
                  density="compact"
                  inset
                  hide-details
                  class="toggle-switch"
                  :class="formData.isActive ? 'on' : 'off'"
                ></v-switch>
            </div>

            <div v-if="formData.interestRateType == 'Home Loan'">
              <!-- MRR Rate -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2" style="color: #333; font-weight: 500;">MRR Rate</div>
                <div class="d-flex align-center" style="gap: 16px;">
                  <v-text-field 
                    v-model.number="formData.mrrRate" 
                    placeholder="MRR Rate %"
                    variant="outlined" 
                    density="comfortable" 
                    type="number"
                    step="0.01"
                    hide-details="auto"
                    bg-color="white"
                    style="flex: 0 1 400px;"
                    class="rounded-input"
                    @input="calculateAllTotalRatesForAllVariants"
                  />
                  <div style="background-color: #e3f2fd; padding: 12px 20px; border-radius: 8px; border: 1px solid #90caf9; white-space: nowrap;">
                    <div style="color: #1976d2; font-size: 14px; font-weight: 500;">MRR Rate = {{ formData.mrrRate || 0 }}%</div>
                    <div style="color: #1976d2; font-size: 11px;">(Current Input)</div>
                  </div>
                </div>
              </div>

              <!-- Variants Section -->
              <div class="mb-4">
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="text-subtitle-2" style="color: #333; font-weight: 500;">ตารางดอกเบี้ย (ตัวเลือก)</div>
                  <v-btn size="small" color="primary" variant="outlined" @click="addVariant">
                    <v-icon start size="small">mdi-plus</v-icon>
                    เพิ่มตัวเลือก
                  </v-btn>
                </div>

                <!-- Variants Expansion Panels -->
                <v-expansion-panels variant="accordion" class="variant-panels">
                  <v-expansion-panel
                    v-for="(variant, variantIndex) in formData.variants"
                    :key="variantIndex"
                    elevation="0"
                    class="variant-panel"
                  >
                    <v-expansion-panel-title class="variant-panel-title">
                      <div class="variant-header">
                        <div class="variant-header-left">
                          <div class="variant-number">
                            <span class="variant-number-text">{{ variantIndex + 1 }}</span>
                          </div>
                          <div class="variant-input-wrapper">
                            <v-icon class="variant-input-icon" size="18">mdi-tag-outline</v-icon>
                            <v-text-field
                              v-model="variant.variantName"
                              placeholder="ชื่อตัวเลือก"
                              variant="plain"
                              density="compact"
                              hide-details
                              class="variant-name-input"
                              @click.stop
                            />
                          </div>
                          <!-- Dropdown for Default Installment Calculation Type -->
                          <div class="variant-input-wrapper ml-4" style="min-width: 320px;">
                            <v-icon class="variant-input-icon" size="18">mdi-calculator-variant</v-icon>
                            <span class="default-calc-label">วิธีคำนวณค่างวดเริ่มต้น:</span>
                            <v-select
                              v-model="variant.installmentType"
                              :items="installmentTypes"
                              item-title="name"
                              item-value="value"
                              variant="plain"
                              density="compact"
                              hide-details
                              class="installment-type-select"
                              @click.stop
                            />
                            <v-tooltip activator="parent" location="top">
                              <span>กำหนดวิธีคำนวณค่างวดเริ่มต้นสำหรับตารางดอกเบี้ยนี้<br/>หากลูกค้าไม่ได้เลือกวิธีคำนวณ ระบบจะใช้ค่านี้</span>
                            </v-tooltip>
                          </div>
                        </div>
                        <div class="variant-header-right">
                          <v-btn
                            v-if="formData.variants.length > 1"
                            class="variant-delete-btn"
                            size="small"
                            variant="tonal"
                            color="error"
                            @click.stop="removeVariant(variantIndex)"
                          >
                            <v-icon size="18">mdi-trash-can-outline</v-icon>
                            <span class="ml-1 d-none d-sm-inline">ลบ</span>
                            <v-tooltip activator="parent" location="top">ลบตัวเลือกนี้</v-tooltip>
                          </v-btn>
                        </div>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <!-- Interest Rate Table for this variant -->
                      <v-card elevation="0" style="border-radius: 12px; overflow: hidden; margin-top: 12px;">
                        <v-table density="comfortable" class="interest-rate-table">
                          <thead>
                            <tr style="background-color: #fafafa;">
                              <th rowspan="2" class="text-left" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; vertical-align: middle; padding: 12px 16px;">
                                Month
                              </th>
                              <th colspan="2" class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px;">
                                Input Interest Rate
                              </th>
                              <th rowspan="2" class="text-right" style="font-weight: 600; color: #666; vertical-align: middle; padding: 12px 16px;">
                                Total Interest Rate
                              </th>
                            </tr>
                            <tr style="background-color: #fafafa;">
                              <th class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px;">
                                Choose Interest Type
                              </th>
                              <th class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px;">
                                Input Interest Difference
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(item, index) in variant.details" :key="index" style="background-color: white;">
                              <td class="text-left" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                                <span style="font-weight: 500; color: #333;">{{ getMonthRangeDisplay(item) }}</span>
                              </td>
                              <td class="text-center" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                                <v-select
                                  v-model="item.interestType"
                                  :items="['Fixed', 'MRR']"
                                  variant="solo"
                                  density="compact"
                                  hide-details
                                  style="max-width: 160px; margin: 0 auto;"
                                  class="interest-type-dropdown"
                                  @update:model-value="calculateTotalRateForVariant(variant, item)"
                                ></v-select>
                              </td>
                              <td class="text-center" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                                <div class="d-flex align-center justify-center gap-2">
                                  <span v-if="item.interestType === 'MRR'" style="color: #666; font-size: 16px;">-</span>
                                  <v-text-field
                                    v-model.number="item.inputInterestDifference"
                                    placeholder="Interest Difference %"
                                    variant="outlined"
                                    density="compact"
                                    type="number"
                                    step="0.01"
                                    hide-details
                                    bg-color="white"
                                    style="max-width: 160px;"
                                    @input="calculateTotalRateForVariant(variant, item)"
                                  />
                                </div>
                              </td>
                              <td class="text-right" style="padding: 12px 16px;">
                                <span style="font-weight: 500; color: #333;">{{ item.totalInterestRate }}</span>
                              </td>
                            </tr>
                          </tbody>
                        </v-table>
                      </v-card>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>

            </div>
            
            <div v-if="formData.interestRateType == 'Personal Loan'">
              <!-- Variants Section for Personal Loan -->
              <div class="mb-4">
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="text-subtitle-2" style="color: #333; font-weight: 500;">ตารางดอกเบี้ย (ตัวเลือก)</div>
                  <v-btn size="small" color="primary" variant="outlined" @click="addVariant">
                    <v-icon start size="small">mdi-plus</v-icon>
                    เพิ่มตัวเลือก
                  </v-btn>
                </div>

                <!-- Variants Expansion Panels -->
                <v-expansion-panels variant="accordion" class="variant-panels">
                  <v-expansion-panel
                    v-for="(variant, variantIndex) in formData.variants"
                    :key="variantIndex"
                    elevation="0"
                    class="variant-panel"
                  >
                    <v-expansion-panel-title class="variant-panel-title">
                      <div class="variant-header">
                        <div class="variant-header-left">
                          <div class="variant-number">
                            <span class="variant-number-text">{{ variantIndex + 1 }}</span>
                          </div>
                          <div class="variant-input-wrapper">
                            <v-icon class="variant-input-icon" size="18">mdi-tag-outline</v-icon>
                            <v-text-field
                              v-model="variant.variantName"
                              placeholder="ชื่อตัวเลือก"
                              variant="plain"
                              density="compact"
                              hide-details
                              class="variant-name-input"
                              @click.stop
                            />
                          </div>
                        </div>
                        <div class="variant-header-right">
                          <v-chip size="small" variant="flat" color="success" class="variant-status-chip">
                            <v-icon start size="12">mdi-check-circle</v-icon>
                            Active
                          </v-chip>
                          <v-btn
                            v-if="formData.variants.length > 1"
                            class="variant-delete-btn"
                            size="small"
                            variant="tonal"
                            color="error"
                            @click.stop="removeVariant(variantIndex)"
                          >
                            <v-icon size="18">mdi-trash-can-outline</v-icon>
                            <span class="ml-1 d-none d-sm-inline">ลบ</span>
                            <v-tooltip activator="parent" location="top">ลบตัวเลือกนี้</v-tooltip>
                          </v-btn>
                        </div>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <!-- Personal Loan Rate Table for this variant -->
                      <v-card elevation="0" style="border: 1px solid #e0e0e0; border-radius: 12px; overflow: hidden; margin-top: 12px;">
                        <v-card-title class="d-flex align-center justify-space-between pa-4" style="background-color: #fafafa;">
                          <span style="font-weight: 600; color: #333;">Personal Loan Interest Rate Table</span>
                          <v-btn size="small" color="primary" variant="outlined" @click="addPersonalLoanMonthForVariant(variantIndex)">
                            <v-icon start size="small">mdi-plus</v-icon>
                            Add Month
                          </v-btn>
                        </v-card-title>
                
                        <div class="personal-loan-scroll-container">
                          <div class="personal-loan-table-wrapper">
                            <table class="personal-loan-table">
                              <thead>
                                <!-- First header row with merged cells -->
                                <tr style="background-color: #fafafa;">
                                  <th rowspan="2" class="text-left" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 12px 16px; min-width: 150px; vertical-align: middle;">
                                    Profession
                                  </th>
                                  <th rowspan="2" class="text-left" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 12px 16px; min-width: 180px; vertical-align: middle;">
                                    Monthly Income Range
                                  </th>
                                  <th :colspan="variant.personalLoanMonths.length" class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px;">
                                    Interest Rate by Month
                                  </th>
                                  <th colspan="2" class="text-center" style="font-weight: 600; color: #666; padding: 8px 16px;">
                                    Actions
                                  </th>
                                </tr>
                                <!-- Second header row with month inputs -->
                                <tr style="background-color: #fafafa;">
                                  <th 
                                    v-for="(month, index) in variant.personalLoanMonths" 
                                    :key="`month-${variantIndex}-${index}`"
                                    class="text-center" 
                                    style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 12px 16px; min-width: 120px;"
                                  >
                                    <div class="d-flex align-center justify-center gap-2">
                                      <v-text-field
                                        v-model="month.value"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        style="max-width: 80px;"
                                        @input="filterNumericInput($event, month)"
                                        @keypress="validateNumericKeypress"
                                      />
                                      <v-btn
                                        icon
                                        size="x-small"
                                        variant="text"
                                        color="error"
                                        @click="removePersonalLoanMonthForVariant(variantIndex, index)"
                                        v-if="variant.personalLoanMonths.length > 1"
                                      >
                                        <v-icon size="small">mdi-close</v-icon>
                                      </v-btn>
                                    </div>
                                  </th>
                                  <th class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px; min-width: 80px;">
                                    Range
                                  </th>
                                  <th class="text-center" style="font-weight: 600; color: #666; padding: 8px 16px; min-width: 80px;">
                                    Profession
                                  </th>
                                </tr>
                              </thead>
                            <tbody>
                              <template v-for="(profession, profIndex) in variant.personalLoanData" :key="`prof-${variantIndex}-${profIndex}`">
                                <tr 
                                  v-for="(range, rangeIndex) in profession.salaryRanges" 
                                  :key="`range-${variantIndex}-${profIndex}-${rangeIndex}`"
                                  style="background-color: white;"
                                >
                                  <!-- Profession Column (merged for first row) -->
                                  <td 
                                    v-if="rangeIndex === 0" 
                                    :rowspan="profession.salaryRanges.length"
                                    class="text-left" 
                                    style="padding: 12px 16px; border-right: 1px solid #e0e0e0; vertical-align: top;"
                                  >
                                    <v-combobox
                                      v-model="profession.occupationId"
                                      :items="occupationsData"
                                      item-title="occupationNameTH"
                                      item-value="occupationID"
                                      variant="outlined"
                                      density="compact"
                                      hide-details
                                      placeholder="Select Profession"
                                      style="min-width: 130px;"
                                    />
                                  </td>
                                  
                                  <!-- Salary Range Column -->
                                  <td class="text-left" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                                    <div class="d-flex align-center gap-2">
                                      <v-text-field
                                        v-model="range.rangeMin"
                                        variant="outlined"
                                        density="compact"
                                        type="number"
                                        hide-details
                                        placeholder="Min"
                                        style="max-width: 100px;"
                                      />
                                      <span style="color: #666;">-</span>
                                      <v-text-field
                                        v-model="range.rangeMax"
                                        variant="outlined"
                                        density="compact"
                                        type="number"
                                        hide-details
                                        placeholder="Max"
                                        style="max-width: 100px;"
                                      />
                                    </div>
                                  </td>
                                  
                                  <!-- Interest Rate Columns (one for each month) -->
                                  <td 
                                    v-for="(month, monthIndex) in variant.personalLoanMonths" 
                                    :key="`rate-${variantIndex}-${profIndex}-${rangeIndex}-${monthIndex}`"
                                    class="text-center" 
                                    style="padding: 12px 16px; border-right: 1px solid #e0e0e0;"
                                  >
                                    <v-text-field
                                      v-model.number="range.interestRates[monthIndex]"
                                      variant="outlined"
                                      density="compact"
                                      type="number"
                                      step="0.01"
                                      hide-details
                                      suffix="%"
                                      placeholder="0.00"
                                      style="max-width: 100px; margin: 0 auto;"
                                    />
                                  </td>
                                  
                                  <!-- Salary Range Actions Column -->
                                  <td class="text-center" style="padding: 4px 8px; border-right: 1px solid #e0e0e0;">
                                    <div class="d-flex gap-1 justify-center">
                                      <v-btn
                                        icon
                                        size="x-small"
                                        variant="text"
                                        color="success"
                                        @click="addSalaryRangeForVariant(variantIndex, profIndex)"
                                        v-if="rangeIndex === profession.salaryRanges.length - 1"
                                      >
                                        <v-icon size="18">mdi-plus</v-icon>
                                        <v-tooltip activator="parent" location="top">Add Range</v-tooltip>
                                      </v-btn>
                                      <v-btn
                                        icon
                                        size="x-small"
                                        variant="text"
                                        color="error"
                                        @click="removeSalaryRangeForVariant(variantIndex, profIndex, rangeIndex)"
                                        v-if="profession.salaryRanges.length > 1"
                                      >
                                        <v-icon size="18">mdi-minus</v-icon>
                                        <v-tooltip activator="parent" location="top">Remove Range</v-tooltip>
                                      </v-btn>
                                    </div>
                                  </td>
                                  
                                  <!-- Profession Actions Column (merged for all rows of same profession) -->
                                  <td 
                                    v-if="rangeIndex === 0" 
                                    :rowspan="profession.salaryRanges.length"
                                    class="text-center" 
                                    style="padding: 4px 8px; vertical-align: middle;"
                                  >
                                    <v-btn
                                      icon
                                      size="x-small"
                                      variant="text"
                                      color="error"
                                      @click="removeProfessionForVariant(variantIndex, profIndex)"
                                    >
                                      <v-icon size="18">mdi-delete</v-icon>
                                      <v-tooltip activator="parent" location="top">Remove Profession</v-tooltip>
                                    </v-btn>
                                  </td>
                                </tr>
                              </template>
                            </tbody>
                          </table>
                          </div>
                        </div>
                        
                        <v-card-actions class="pa-4" style="background-color: #fafafa;">
                          <v-btn
                            color="primary"
                            variant="outlined"
                            @click="addProfessionForVariant(variantIndex)"
                          >
                            <v-icon start>mdi-plus</v-icon>
                            Add Profession
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </div>
            

          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="closeDialog">Close</v-btn>
          <v-btn
            v-if="editMode ? permissions.edit : permissions.add"
            class="btn-save"
            :disabled="!formValid || !formData.effectiveStartDate"
            @click="saveAccountInfo()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Categories Management Dialog -->
    <v-dialog v-model="categoriesDialog" max-width="900px">
      <v-card class="add-proactive-dialog">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center">
            <div class="header-icon-container">
              <v-avatar class="icon-background" size="46">
                <v-icon color="#1976d2" size="24">mdi-shape</v-icon>
              </v-avatar>
            </div>
            <div>
              <div class="dialog-title">Category Management</div>
              <div class="dialog-subtitle">Manage interest rate categories</div>
            </div>
          </div>
        </v-card-title>

        <v-card-text class="pa-6">
          <!-- Add Category Form -->
          <v-form ref="categoryForm" v-model="categoryFormValid">
            <div style="background-color: #eeeff3; padding: 20px; border-radius: 8px; margin-bottom: 24px;">
              <div class="text-subtitle-2 mb-3" style="color: #333; font-weight: 500;">
                {{ !categoryEditMode ? 'Add New Category' : 'Update Category' }}
              </div>
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="categoryFormData.categoryName"
                    label="Category Name"
                    placeholder="Enter category name"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    bg-color="white"
                    class="rounded-input"
                  />
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-end">
                  <v-btn
                    v-if="!categoryEditMode && permissions.add"
                    class="btn-save w-100"
                    :disabled="!categoryFormValid || !categoryFormData.categoryName"
                    @click="addCategory"
                  >
                    <v-icon start>mdi-plus</v-icon>
                    Add Category
                  </v-btn>
                  <div v-else style="width: 100%; display: flex; gap: 12px; align-items: center;">
                    <v-btn
                      v-if="permissions.edit"
                      class="btn-save flex-grow-1"
                      :disabled="!categoryFormValid || !categoryFormData.categoryName"
                      @click="updateCategory"
                    >
                      <v-icon start>mdi-content-save</v-icon>
                      Update
                    </v-btn>
                    <v-btn
                      variant="outlined"
                      color="grey"
                      @click="cancelCategoryEdit"
                    >
                      <v-icon>mdi-close</v-icon>
                      <v-tooltip activator="parent" location="top">Cancel</v-tooltip>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-form>

          <!-- Categories List -->
          <v-data-table
            :headers="categoryHeaders"
            :items="categoriesData"
            :loading="loadingCategories"
            class="elevation-1"
            density="comfortable"
            :items-per-page="5"
          >
            <template v-slot:item.categoryName="{ item }">
              <v-chip :color="getCategoryColorById(item.categoryID)" size="small" variant="outlined">
                {{ item.categoryName }}
              </v-chip>
            </template>

            <template v-slot:item.isActive="{ item }">
              <v-chip :color="item.isActive ? 'success' : 'error'" size="small">
                {{ item.isActive ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="action-buttons-group">
                <v-btn
                  size="small"
                  variant="outlined"
                  color="primary"
                  @click="editCategory(item)"
                  class="action-btn"
                >
                  <v-icon v-if="!permissions.edit" size="16" color="primary">mdi-eye-outline</v-icon>
                  <v-img v-else :src="images.EditImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">{{ permissions.edit ? 'Edit' : 'View' }}</v-tooltip>
                </v-btn>
                <v-btn
                  v-if="permissions.delete"
                  size="small"
                  variant="outlined"
                  color="error"
                  @click="confirmDeleteCategory(item)"
                  class="action-btn"
                >
                  <v-img :src="images.DeleteImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="closeCategoriesDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- duplicate dialog -->
    <v-dialog v-model="dialog_duplicate" persistent max-width="600px" class="modern-dialog">
      <v-card>
          <div class="dialog-header-doc">
            <div class="header-main">
              <div>
                <h2 style="font-weight: 600;">Duplicate Loan Interest Rate</h2>
              </div>
            </div>
            <div class="header-actions">
              <v-btn icon variant="text" @click="dialog_duplicate = false" class="close-btn">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        <v-card-text class="pt-3 pl-4 pr-4 pb-1">
          <v-container>
            <v-form ref="form" lazy-validation>
              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <span>Set a name for the duplicate interest rate</span>
                  <v-text-field id="input-duplicateInterestRateName" @keypress="handleKeypress" label="Interest Rate Name" color="#C32E0E"
                    v-model="newDuplicateInterestRateName" :rules="[rules.required]" required variant="outlined" class="modern-input"></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions d-flex justify-end">
          <v-btn text @click="duplicate_dialog_close" class="delete-cancel-btn-1" color="error">
            <v-icon>mdi-close</v-icon>
            {{$t("close")}}
          </v-btn>
          <v-btn v-if="permissions.add" id="button-save-duplicateInterestRateName" class="dialog-action-btn save-btn-edit" text @click="SaveDuplicateInterest">
            <img src="@/assets/mage_save-btn.svg" alt="Save" class="white-svg" />
            {{$t("save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import editImg from '@/assets/edit1.png';
import deleteImg from '@/assets/delete1.png';

const DEFAULT_PERSONAL_LOAN_MONTH_VALUES = [12, 24, 36, 48, 60];

const createDefaultPersonalLoanMonths = () =>
  DEFAULT_PERSONAL_LOAN_MONTH_VALUES.map((value) => ({ value }));

const createDefaultPersonalLoanData = (monthsLength = DEFAULT_PERSONAL_LOAN_MONTH_VALUES.length) => [
  {
    occupationId: null,
    salaryRanges: [
      {
        rangeMin: null,
        rangeMax: null,
        interestRates: [],
      },
    ],
  },
];

const createDefaultHomeLoanDetails = () => [
  {
    monthRangeStart: 1,
    monthRangeEnd: 12,
    interestType: 'Fixed',
    inputInterestDifference: 0,
    totalInterestRate: '0.000%',
  },
  {
    monthRangeStart: 13,
    monthRangeEnd: 24,
    interestType: 'MRR',
    inputInterestDifference: 0,
    totalInterestRate: '0.000%',
  },
  {
    monthRangeStart: 25,
    monthRangeEnd: 36,
    interestType: 'MRR',
    inputInterestDifference: 0,
    totalInterestRate: '0.000%',
  },
  {
    monthRangeStart: 37,
    monthRangeEnd: null,
    interestType: 'MRR',
    inputInterestDifference: 0,
    totalInterestRate: '0.000%',
  },
];

export default {
  name: 'AccountInfoManagement',
  inject: ['flagNav', 'FontColorTitle'],
  data() {
    const defaultPersonalLoanMonths = createDefaultPersonalLoanMonths();
    return {
      today: null,
      loading: false,
      loadingDialog: false,
      dialog: false,
      editMode: false,
      saving: false,
      formValid: false,
      search: '',
      categoryFilter: null,
      startDateMenu: false,
      endDateMenu: false,
      loadingCategories: false,

      //Duplicate dialog
      dialog_duplicate: false,
      newDuplicateInterestRateName: '',
      duplicateItem: null,

      // Categories Dialog
      categoriesDialog: false,
      categoryFormValid: false,
      categoryEditMode: false,
      categoryFormData: {
        categoryId: 0,
        categoryName: '',
      },

      images: {
        EditImg: editImg,
        DeleteImg: deleteImg,
      },

      // Data from API
      interestRatesData: [],
      categoriesData: [],
      occupationsData: [],

      formData: {
        interestRateID: 0,
        interestRateName: '',
        interestRateType: '',
        categoryID: null,
        categoryName: '',
        effectiveStartDate: '',
        effectiveEndDate: '',
        isActive: true,
        mrrRate: 0,
        variants: [
          {
            variantID: 0,
            variantName: 'ตัวเลือกเริ่มต้น',
            variantDescription: '',
            sortOrder: 1,
            isActive: true,
            installmentType: 'Normal',
            details: createDefaultHomeLoanDetails(),
            // Personal Loan Data
            personalLoanMonths: defaultPersonalLoanMonths,
            personalLoanData: createDefaultPersonalLoanData(defaultPersonalLoanMonths.length),
          }
        ],
      },

      interestRateTypes: ['Home Loan', 'Personal Loan', 'Car Loan'],

      // Installment Types for Home Loan variants
      installmentTypes: [
        { value: 'Normal', name: 'ค่างวดคงที่ตลอดอายุสินเชื่อ' },
        { value: 'LowInstallment3Years', name: 'ค่างวดการผ่อนต่ำ 3 ปีแรก ตามดอกเบี้ยปกติ' },
        { value: 'FrontLowPerMillion', name: 'ค่างวดการผ่อนหน้าต่ำ ล้านละ' },
      ],

      rules: {
        required: (value) => {
          if (value === 0) return true;
          if (value === null || value === undefined) return 'Required.';
          if (typeof value === 'boolean') return true;
          if (Array.isArray(value)) return value.length > 0 || 'Required.';
          if (typeof value === 'string') return !this.IsWhiteSpace(value) || 'Required.';
          if (typeof value === 'object') return Object.keys(value).length > 0 || 'Required.';
          return !!value || 'Required.';
        },
        effectiveDateRequired: () => {
          if (this.formData.effectiveStartDate) return true;
          return 'Effective start date is required.';
        },
      },

      headers: [
        { title: 'No.', key: 'number', sortable: false },
        { title: 'Interest rate Name', key: 'interestRateName', sortable: true },
        { title: 'Type', key: 'interestRateType', sortable: true },
        { title: 'Category', key: 'categoryID', sortable: true },
        { title: 'Effective Start Date', key: 'effectiveStartDate', sortable: false },
        { title: 'Effective End Date', key: 'effectiveEndDate', sortable: false },
        { title: 'Active Status', key: 'isActive', sortable: false },
        { title: '', key: 'duplicate', sortable: false, class: 'sticky-action' },
        { title: 'Actions', key: 'actions', sortable: false, class: 'sticky-action' },
      ],

      categoryHeaders: [
        { title: 'Category Name', key: 'categoryName', sortable: true },
        { title: 'Status', key: 'isActive', sortable: true },
        { title: 'Actions', key: 'actions', sortable: false },
      ],
    };
  },

  computed: {
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('LoanInterestRate');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('LoanInterestRate'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('LoanInterestRate'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('LoanInterestRate'));
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('LoanInterestRate'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('LoanInterestRate'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('LoanInterestRate'),
      }
    },
    filteredItems() {
      let items = this.interestRatesData.map((item, index) => ({
        ...item,
        number: index + 1
      }));

      if (this.search) {
        items = items.filter(
          (item) =>
            item.interestRateName?.toLowerCase().includes(this.search.toLowerCase()) ||
            item.interestRateType?.toLowerCase().includes(this.search.toLowerCase()) ||
            item.categoryName?.toLowerCase().includes(this.search.toLowerCase())
        );
      }

      if (this.categoryFilter) {
        items = items.filter((item) => item.categoryID === this.categoryFilter);
      }

      return items;
    },
    startDateMax() {
      if (!this.formData.effectiveEndDate) return null;
      return this.shiftDateByDays(this.formData.effectiveEndDate, -1);
    },
    endDateMin() {
      if (!this.formData.effectiveStartDate) return null;
      return this.shiftDateByDays(this.formData.effectiveStartDate, 1);
    },
  },

  async mounted() {
    this.GetDate();
    await this.loadCategories();
    await this.loadInterestRates();
  },

  methods: {
    IsWhiteSpace(value) {
      return typeof value === 'string' && value.trim().length === 0;
    },

    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1;
      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = '0' + dd;
      }
      if (mm < 10) {
        mm = '0' + mm;
      }
      this.today = dd + '/' + mm + '/' + yyyy;
    },

    popupDuplicate(interestRate) {
      let self = this;
      self.dialog_duplicate = true;
      self.newDuplicateInterestRateName = null;
      self.duplicateItem = interestRate;
    },
    duplicate_dialog_close() {
      this.dialog_duplicate = false;
      this.newDuplicateInterestRateName = '';
      this.duplicateItem = null;
    },

    // API Methods
    async loadCategories() {
      this.loadingCategories = true;
      try {
        await new Promise((resolve, reject) => {
          this.$call.GetInterestRateCategories(
            (response) => {
              if (response && response.data) {
                this.categoriesData = response.data.interestRateCategories;
                this.occupationsData = response.data.occupations || [];
              }
              resolve(response);
            },
            (error) => {
              console.error('Error loading categories:', error);
              this.$swal({
                title: 'Error!',
                text: 'Failed to load categories',
                icon: 'error',
                confirmButtonColor: '#d33'
              });
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error('Error in loadCategories:', error);
      } finally {
        this.loadingCategories = false;
      }
    },

    async loadInterestRates() {
      this.loading = true;
      try {
        await new Promise((resolve, reject) => {
          this.$call.GetInterestRates(
            (response) => {
              if (response && response.data) {
                this.interestRatesData = response.data;
              }
              resolve(response);
            },
            (error) => {
              console.error('Error loading interest rates:', error);
              this.$swal({
                title: 'Error!',
                text: 'Failed to load interest rates',
                icon: 'error',
                confirmButtonColor: '#d33'
              });
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error('Error in loadInterestRates:', error);
      } finally {
        this.loading = false;
      }
    },

    formatDateDisplay(date) {
      if (!date) return '';
      
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      
      return `${day}/${month}/${year}`;
    },

    shiftDateByDays(dateValue, days) {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return null;
      date.setDate(date.getDate() + days);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    onStartDateChange(value) {
      this.startDateMenu = false;
      
      // Convert Date object to YYYY-MM-DD string
      if (value) {
        const d = new Date(value);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        this.formData.effectiveStartDate = `${year}-${month}-${day}`;
      }
      
      // If end date exists and is before or equal to the new start date, clear end date
      if (this.formData.effectiveEndDate && this.formData.effectiveStartDate) {
        const startDate = new Date(this.formData.effectiveStartDate);
        const endDate = new Date(this.formData.effectiveEndDate);
        
        if (startDate >= endDate) {
          this.formData.effectiveEndDate = null;
        }
      }

      this.$refs.form?.validate();
    },

    onEndDateChange(value) {
      this.endDateMenu = false;
      
      // Convert Date object to YYYY-MM-DD string
      if (value) {
        const d = new Date(value);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        this.formData.effectiveEndDate = `${year}-${month}-${day}`;
      }
      
      // Enforce at least 1 day gap: end date must be after start date
      if (this.formData.effectiveStartDate && this.formData.effectiveEndDate) {
        const startDate = new Date(this.formData.effectiveStartDate);
        const endDate = new Date(this.formData.effectiveEndDate);
        
        if (endDate <= startDate) {
          this.formData.effectiveStartDate = null;
        }
      }

      this.$refs.form?.validate();
    },

    onInterestRateTypeChange(newType) {
      // Reset to default values when type changes to Home Loan
      if (newType === 'Home Loan') {
        this.formData.mrrRate = 0;
        this.formData.variants = [
          {
            variantID: 0,
            variantName: 'ตัวเลือกเริ่มต้น',
            variantDescription: '',
            sortOrder: 1,
            isActive: true,
            details: createDefaultHomeLoanDetails(),
          }
        ];
        this.calculateAllTotalRatesForAllVariants();
      }
      // Reset Personal Loan data when changing to Personal Loan
      else if (newType === 'Personal Loan') {
        const defaultMonths = createDefaultPersonalLoanMonths();
        this.formData.variants = [
          {
            variantID: 0,
            variantName: 'ตัวเลือกเริ่มต้น',
            variantDescription: '',
            sortOrder: 1,
            isActive: true,
            personalLoanMonths: defaultMonths,
            personalLoanData: createDefaultPersonalLoanData(defaultMonths.length),
          }
        ];
      }
    },

    onInterestRateTypeChangeAndValidate(newType) {
      if (this.IsWhiteSpace(newType)) {
        this.formData.interestRateType = null;
        this.$nextTick(() => {
          this.$refs.interestRateTypeField?.validate();
        });
        return;
      }

      this.onInterestRateTypeChange(newType);
      this.$nextTick(() => {
        this.$refs.interestRateTypeField?.validate();
      });
    },

    calculateTotalRateForVariant(variant, item) {
      const mrrRate = parseFloat(this.formData.mrrRate) || 0;
      const difference = parseFloat(item.inputInterestDifference) || 0;
      
      let totalRate = 0;
      
      if (item.interestType === 'Fixed') {
        // Fixed: Total = Input Interest Difference
        totalRate = difference;
      } else if (item.interestType === 'MRR') {
        // MRR: Total = MRR Rate - Input Interest Difference
        totalRate = mrrRate - difference;
      }
      
      // Format to 3 decimal places with % sign
      item.totalInterestRate = totalRate.toFixed(3) + '%';
    },

    calculateAllTotalRatesForAllVariants() {
      if (!this.formData.variants || !Array.isArray(this.formData.variants)) {
        return;
      }
      // Recalculate all variants when MRR Rate changes
      this.formData.variants.forEach(variant => {
        if (variant.details && Array.isArray(variant.details)) {
          variant.details.forEach(item => {
            this.calculateTotalRateForVariant(variant, item);
          });
        }
      });
    },

    // Variant Management Methods
    addVariant() {
      const newVariant = {
        variantID: 0,
        variantName: `ตัวเลือก ${this.formData.variants.length + 1}`,
        variantDescription: '',
        sortOrder: this.formData.variants.length + 1,
        isActive: true,
        installmentType: 'Normal',
      };

      if (this.formData.interestRateType === 'Home Loan') {
        newVariant.details = createDefaultHomeLoanDetails();
        this.calculateAllTotalRatesForAllVariants();
      } else if (this.formData.interestRateType === 'Personal Loan') {
        const defaultMonths = createDefaultPersonalLoanMonths();
        newVariant.personalLoanMonths = defaultMonths;
        newVariant.personalLoanData = createDefaultPersonalLoanData(defaultMonths.length);
      }

      this.formData.variants.push(newVariant);
    },

    removeVariant(variantIndex) {
      if (this.formData.variants.length <= 1) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Cannot Delete',
          text: 'ต้องมีอย่างน้อย 1 ตัวเลือก',
        });
        return;
      }

      this.$swal.fire({
        title: 'ยืนยันการลบ?',
        text: `คุณต้องการลบ "${this.formData.variants[variantIndex].variantName}" หรือไม่?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'ลบ',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          this.formData.variants.splice(variantIndex, 1);
          // Update sort orders
          this.formData.variants.forEach((v, idx) => {
            v.sortOrder = idx + 1;
          });
        }
      });
    },

    async toggleActive(item, value) {
      try {
        const updateRequest = {
          interestRateName: item.interestRateName,
          interestRateType: item.interestRateType,
          categoryID: item.categoryID,
          effectiveStartDate: item.effectiveStartDate,
          effectiveEndDate: item.effectiveEndDate,
          isActive: value,
          mrrRate: item.mrrRate,
          details: item.details,
        };
        const index = this.interestRatesData.findIndex(i => i.interestRateID === item.interestRateID);
        if (index !== -1) {
          this.interestRatesData[index].isActive = value;
        }
        await new Promise((resolve, reject) => {
          this.$call.UpdateInterestRate(item.interestRateID, updateRequest,
            (response) => {
              this.$swal({
                title: 'Success!',
                text: 'Status updated successfully',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
              });
              // Update the item in the array to trigger reactivity
              
              resolve(response);
            },
            (error) => {
              console.error('Error updating status:', error);
              this.$swal({
                title: 'Error!',
                text: 'Failed to update status',
                icon: 'error',
                confirmButtonColor: '#d33'
              });
              this.interestRatesData[index].isActive = !value;
              // Reload data to revert changes
              this.loadInterestRates();
              reject(error);
            }
          );
        });
      } catch (error) {
        // Reload data to revert changes
        this.loadInterestRates();
      }
    },

    getCategoryColorById(categoryID) {
      // Return color based on category
      const colors = ['purple', 'blue', 'green', 'orange', 'teal'];
      const index = (categoryID - 1) % colors.length;
      return colors[index] || 'grey';
    },

    getCategoryNameById(categoryID) {
      // Return category name based on ID from API data
      const category = this.categoriesData.find(cat => cat.categoryID === categoryID);
      return category ? category.categoryName : 'Unknown';
    },

    // Personal Loan Methods for Variants
    addPersonalLoanMonthForVariant(variantIndex) {
      const variant = this.formData.variants[variantIndex];
      variant.personalLoanMonths.push({ value: '' });
      // Add empty rate column to all existing data
      variant.personalLoanData.forEach(profession => {
        profession.salaryRanges.forEach(range => {
          range.interestRates.push(null);
        });
      });
    },

    removePersonalLoanMonthForVariant(variantIndex, monthIndex) {
      const variant = this.formData.variants[variantIndex];
      if (variant.personalLoanMonths.length <= 1) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Cannot Delete',
          text: 'Must have at least one month column',
        });
        return;
      }
      variant.personalLoanMonths.splice(monthIndex, 1);
      // Remove corresponding rate column from all data
      variant.personalLoanData.forEach(profession => {
        profession.salaryRanges.forEach(range => {
          range.interestRates.splice(monthIndex, 1);
        });
      });
    },

    addProfessionForVariant(variantIndex) {
      const variant = this.formData.variants[variantIndex];
      variant.personalLoanData.push({
        occupationId: null,
        salaryRanges: [
          {
            rangeMin: null,
            rangeMax: null,
            interestRates: new Array(variant.personalLoanMonths.length).fill(null),
          },
        ],
      });
    },

    removeProfessionForVariant(variantIndex, profIndex) {
      const variant = this.formData.variants[variantIndex];
      if (variant.personalLoanData.length <= 1) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Cannot Delete',
          text: 'Must have at least one profession',
        });
        return;
      }
      variant.personalLoanData.splice(profIndex, 1);
    },

    addSalaryRangeForVariant(variantIndex, profIndex) {
      const variant = this.formData.variants[variantIndex];
      variant.personalLoanData[profIndex].salaryRanges.push({
        rangeMin: null,
        rangeMax: null,
        interestRates: new Array(variant.personalLoanMonths.length).fill(null),
      });
    },

    removeSalaryRangeForVariant(variantIndex, profIndex, rangeIndex) {
      const variant = this.formData.variants[variantIndex];
      const profession = variant.personalLoanData[profIndex];
      if (profession.salaryRanges.length <= 1) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Cannot Delete',
          text: 'Must have at least one salary range',
        });
        return;
      }
      profession.salaryRanges.splice(rangeIndex, 1);
    },

    transformPersonalLoanRatesToUi(personalLoanRates) {
      if (!Array.isArray(personalLoanRates) || !personalLoanRates.length) {
        return null;
      }

      const monthValues = Array.from(
        new Set(
          personalLoanRates
            .map((rate) => Number(rate.tenorMonths ?? rate.tenorMonth))
            .filter((value) => !Number.isNaN(value) && value > 0)
        )
      ).sort((a, b) => a - b);

      if (!monthValues.length) {
        return null;
      }

      const monthIndexMap = new Map(monthValues.map((value, index) => [value, index]));
      const professionRangeMap = new Map();

      personalLoanRates.forEach((rate) => {
        const occupationIdRaw = rate.occupationID ?? rate.occupationId;
        const occupationId = Number(occupationIdRaw);
        const tenorValue = Number(rate.tenorMonths ?? rate.tenorMonth);

        if (Number.isNaN(occupationId) || Number.isNaN(tenorValue) || !monthIndexMap.has(tenorValue)) {
          return;
        }

        if (!professionRangeMap.has(occupationId)) {
          professionRangeMap.set(occupationId, new Map());
        }

        const incomeMin = rate.incomeMin !== undefined && rate.incomeMin !== null ? Number(rate.incomeMin) : null;
        const incomeMax = rate.incomeMax !== undefined && rate.incomeMax !== null ? Number(rate.incomeMax) : null;
        const rangeKey = `${incomeMin ?? 'null'}-${incomeMax ?? 'null'}`;
        const salaryRangeCollection = professionRangeMap.get(occupationId);

        if (!salaryRangeCollection.has(rangeKey)) {
          salaryRangeCollection.set(rangeKey, {
            rangeMin: incomeMin,
            rangeMax: incomeMax,
            interestRates: Array(monthValues.length).fill(0),
          });
        }

        const rangeEntry = salaryRangeCollection.get(rangeKey);
        const interestValueRaw = rate.interestRate ?? rate.rate;
        const interestValue = Number(interestValueRaw);
        rangeEntry.interestRates[monthIndexMap.get(tenorValue)] = Number.isNaN(interestValue) ? 0 : interestValue;
      });

      const personalLoanData = Array.from(professionRangeMap.entries())
        .map(([occupationId, ranges]) => ({
          occupationId,
          salaryRanges: Array.from(ranges.values()).sort(
            (a, b) => (a.rangeMin ?? 0) - (b.rangeMin ?? 0)
          ),
        }))
        .sort((a, b) => a.occupationId - b.occupationId);

      return {
        personalLoanMonths: monthValues.map((value) => ({ value: String(value) })),
        personalLoanData,
      };
    },

    buildPersonalLoanRatesPayloadForVariant(variant) {
      const payload = [];

      (variant.personalLoanData || []).forEach((profession) => {
        // Handle both object (from dropdown) and direct ID value
        let occupationId;
        if (typeof profession.occupationId === 'object' && profession.occupationId !== null) {
          occupationId = Number(profession.occupationId.occupationID);
        } else {
          occupationId = Number(profession.occupationId);
        }
        
        if (Number.isNaN(occupationId) || occupationId <= 0) {
          return;
        }
        
        (profession.salaryRanges || []).forEach((range) => {
          const hasMin = range.rangeMin !== undefined && range.rangeMin !== null && range.rangeMin !== '';
          if (!hasMin) {
            return;
          }

          const incomeMin = Number(range.rangeMin);
          if (Number.isNaN(incomeMin)) {
            return;
          }

          const hasMax = range.rangeMax !== undefined && range.rangeMax !== null && range.rangeMax !== '';
          const incomeMax = hasMax ? Number(range.rangeMax) : null;
          if (hasMax && Number.isNaN(incomeMax)) {
            return;
          }

          const interestRates = Array.isArray(range.interestRates) ? range.interestRates : [];

          (variant.personalLoanMonths || []).forEach((month, monthIndex) => {
            const monthValue = Number(month.value);
            if (Number.isNaN(monthValue) || monthValue <= 0) {
              return;
            }

            const rateValueRaw = interestRates[monthIndex];
            if (rateValueRaw === undefined || rateValueRaw === null || rateValueRaw === '') {
              return;
            }

            const rateValue = Number(rateValueRaw);
            if (Number.isNaN(rateValue)) {
              return;
            }

            payload.push({
              occupationID: occupationId,
              incomeMin,
              incomeMax,
              tenorMonths: monthValue,
              interestRate: rateValue,
              isActive: this.formData.isActive,
              interestRateID: this.formData.interestRateID || 0,
            });
          });
        });
      });

      return payload;
    },

    buildAllVariantsPayload() {
      if (!this.formData.variants || !Array.isArray(this.formData.variants)) {
        return [];
      }

      return this.formData.variants.map(variant => ({
        variantID: variant.variantID || 0,
        variantName: variant.variantName,
        variantDescription: variant.variantDescription || '',
        sortOrder: variant.sortOrder,
        isActive: variant.isActive !== false,
        installmentType: variant.installmentType || 'Normal',
        // For Home Loan
        details: this.formData.interestRateType === 'Home Loan' ? 
          (variant.details || []).map(detail => ({
            monthRange: `${detail.monthRangeStart} - ${detail.monthRangeEnd || 'And Above'}`,
            monthRangeStart: detail.monthRangeStart,
            monthRangeEnd: detail.monthRangeEnd,
            interestType: detail.interestType,
            inputInterestDifference: parseFloat(detail.inputInterestDifference) || 0
          })) : undefined,
        // For Personal Loan
        personalLoanRates: this.formData.interestRateType === 'Personal Loan' ?
          this.buildPersonalLoanRatesPayloadForVariant(variant) : undefined
      }));
    },

    getMonthRangeDisplay(item) {
      // Format month range display
      if (!item.monthRangeEnd) {
        return `${item.monthRangeStart} And Above`;
      }
      return `${item.monthRangeStart}-${item.monthRangeEnd}`;
    },

    openCategoriesDialog() {
      this.categoriesDialog = true;
      this.loadCategories();
    },

    closeCategoriesDialog() {
      this.categoriesDialog = false;
      this.categoryEditMode = false;
      this.categoryFormData = {
        categoryId: 0,
        categoryName: '',
      };
      this.$refs.categoryForm?.resetValidation();
    },

    async addCategory() {
      if (!this.categoryFormValid) return;

      this.loadingCategories = true;
      try {
        await new Promise((resolve, reject) => {
          this.$call.AddInterestRateCategory(
            { categoryName: this.categoryFormData.categoryName },
            (response) => {
              this.$swal({
                title: 'Success!',
                text: 'Category created successfully',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
              });
              this.loadCategories();
              this.categoryFormData.categoryName = '';
              this.$refs.categoryForm?.resetValidation();
              resolve(response);
            },
            (error) => {
              console.error('Error creating category:', error);
              const errorMsg = error.response?.data?.message || 'Failed to create category';
              this.$swal({
                title: 'Error!',
                text: errorMsg,
                icon: 'error',
                confirmButtonColor: '#d33'
              });
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error('Error in addCategory:', error);
      } finally {
        this.loadingCategories = false;
      }
    },

    editCategory(item) {
      this.categoryEditMode = true;
      this.categoryFormData = {
        categoryId: item.categoryID,
        categoryName: item.categoryName,
      };
    },

    cancelCategoryEdit() {
      this.categoryEditMode = false;
      this.categoryFormData = {
        categoryId: 0,
        categoryName: '',
      };
      this.$refs.categoryForm?.resetValidation();
    },

    async updateCategory() {
      if (!this.categoryFormValid) return;

      this.loadingCategories = true;
      try {
        await new Promise((resolve, reject) => {
          this.$call.UpdateInterestRateCategory(
            this.categoryFormData.categoryId,
            { categoryName: this.categoryFormData.categoryName },
            (response) => {
              this.$swal({
                title: 'Success!',
                text: 'Category updated successfully',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
              });
              this.loadCategories();
              this.cancelCategoryEdit();
              resolve(response);
            },
            (error) => {
              console.error('Error updating category:', error);
              const errorMsg = error.response?.data?.message || 'Failed to update category';
              this.$swal({
                title: 'Error!',
                text: errorMsg,
                icon: 'error',
                confirmButtonColor: '#d33'
              });
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error('Error in updateCategory:', error);
      } finally {
        this.loadingCategories = false;
      }
    },

    confirmDeleteCategory(item) {
      this.$swal({
        title: 'Are you sure?',
        text: `Do you want to delete category "${item.categoryName}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteCategory(item);
        }
      });
    },

    async deleteCategory(item) {
      this.loadingCategories = true;
      try {
        await new Promise((resolve, reject) => {
          this.$call.DeleteInterestRateCategory(
            item.categoryID,
            (response) => {
              this.$swal({
                title: 'Deleted!',
                text: 'Category deleted successfully',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
              });
              this.loadCategories();
              resolve(response);
            },
            (error) => {
              console.error('Error deleting category:', error);
              const errorMsg = error.response?.data?.message || 'Failed to delete category';
              this.$swal({
                title: 'Error!',
                text: errorMsg,
                icon: 'error',
                confirmButtonColor: '#d33'
              });
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error('Error in deleteCategory:', error);
      } finally {
        this.loadingCategories = false;
      }
    },

    async openDialog(item = null) {
      this.editMode = !!item;
      if (item) {
        // Load detail data for editing
        try {
          await new Promise((resolve, reject) => {
            this.$call.GetInterestRateById(item.interestRateID,
              (response) => {
                if (response && response.data) {
                  // Convert dates to YYYY-MM-DD format
                  const formatDate = (date) => {
                    if (!date) return null;
                    const d = new Date(date);
                    const year = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  };
                  
                  // Transform variants or create default
                  let variants = [];
                  if (response.data.variants && Array.isArray(response.data.variants) && response.data.variants.length > 0) {
                    // Load existing variants
                    variants = response.data.variants.map(v => {
                      const variant = {
                        variantID: v.variantID,
                        variantName: v.variantName,
                        variantDescription: v.variantDescription || '',
                        sortOrder: v.sortOrder,
                        isActive: v.isActive,
                        installmentType: v.installmentType || 'Normal',
                      };

                      if (response.data.interestRateType === 'Home Loan') {
                        variant.details = v.details || createDefaultHomeLoanDetails();
                      } else if (response.data.interestRateType === 'Personal Loan') {
                        const transformed = this.transformPersonalLoanRatesToUi(v.personalLoanRates || []);
                        if (transformed) {
                          variant.personalLoanMonths = transformed.personalLoanMonths;
                          variant.personalLoanData = transformed.personalLoanData;
                        } else {
                          const defaultMonths = createDefaultPersonalLoanMonths();
                          variant.personalLoanMonths = defaultMonths;
                          variant.personalLoanData = createDefaultPersonalLoanData(defaultMonths.length);
                        }
                      }

                      return variant;
                    });
                  } else {
                    // Legacy data without variants - create one default variant
                    const defaultVariant = {
                      variantID: 0,
                      variantName: 'ตัวเลือกเริ่มต้น',
                      variantDescription: '',
                      sortOrder: 1,
                      isActive: true,
                      installmentType: 'Normal',
                    };

                      if (response.data.interestRateType === 'Home Loan') {
                        defaultVariant.details = response.data.details || createDefaultHomeLoanDetails();
                      } else if (response.data.interestRateType === 'Personal Loan') {
                        const transformed = this.transformPersonalLoanRatesToUi(response.data.personalLoanRates || []);
                        if (transformed) {
                          defaultVariant.personalLoanMonths = transformed.personalLoanMonths;
                          defaultVariant.personalLoanData = transformed.personalLoanData;
                        } else {
                          const defaultMonths = createDefaultPersonalLoanMonths();
                          defaultVariant.personalLoanMonths = defaultMonths;
                          defaultVariant.personalLoanData = createDefaultPersonalLoanData(defaultMonths.length);
                        }
                      }

                    variants = [defaultVariant];
                  }                  this.formData = {
                    ...response.data,
                    effectiveStartDate: formatDate(response.data.effectiveStartDate),
                    effectiveEndDate: formatDate(response.data.effectiveEndDate),
                    variants: variants,
                  };
                  
                  this.calculateAllTotalRatesForAllVariants();
                }
                resolve(response);
              },
              (error) => {
                console.error('Error loading interest rate detail:', error);
                this.$swal({
                  title: 'Error!',
                  text: 'Failed to load interest rate details',
                  icon: 'error',
                  confirmButtonColor: '#d33'
                });
                reject(error);
              }
            );
          });
        } catch (error) {
          return;
        }
      } else {
        const defaultPersonalLoanMonths = createDefaultPersonalLoanMonths();
        this.formData = {
          interestRateID: 0,
          interestRateName: '',
          interestRateType: null,
          categoryID: null,
          categoryName: '',
          effectiveStartDate: null,
          effectiveEndDate: null,
          isActive: true,
          mrrRate: 0,
          variants: [
            {
              variantID: 0,
              variantName: 'ตัวเลือกเริ่มต้น',
              variantDescription: '',
              sortOrder: 1,
              isActive: true,
              details: createDefaultHomeLoanDetails(),
              personalLoanMonths: defaultPersonalLoanMonths,
              personalLoanData: createDefaultPersonalLoanData(defaultPersonalLoanMonths.length),
            }
          ],
        };
      }
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      const defaultPersonalLoanMonths = createDefaultPersonalLoanMonths();
      this.formData = {
        interestRateID: 0,
        interestRateName: '',
        interestRateType: '',
        categoryID: null,
        categoryName: '',
        effectiveStartDate: '',
        effectiveEndDate: '',
        isActive: true,
        mrrRate: 0,
        variants: [
          {
            variantID: 0,
            variantName: 'ตัวเลือกเริ่มต้น',
            variantDescription: '',
            sortOrder: 1,
            isActive: true,
            details: createDefaultHomeLoanDetails(),
            personalLoanMonths: defaultPersonalLoanMonths,
            personalLoanData: createDefaultPersonalLoanData(defaultPersonalLoanMonths.length),
          }
        ],
      };
      this.$refs.form?.resetValidation();
    },

    async saveAccountInfo(duplicateItem = null) {
      if (!duplicateItem) {
        const { valid } = await this.$refs.form.validate();
        if (!valid) return;
      }
      
      this.saving = true;
      this.loadingDialog = true;
      
      try {
        // If duplicating, use the duplicate item data
        const sourceData = duplicateItem || this.formData;

        const requestData = {
          interestRateName: sourceData.interestRateName,
          interestRateType: sourceData.interestRateType,
          categoryID: parseInt(sourceData.categoryID) || 0,
          effectiveStartDate: sourceData.effectiveStartDate,
          effectiveEndDate: sourceData.effectiveEndDate,
          isActive: sourceData.isActive,
          mrrRate: parseFloat(sourceData.mrrRate) || 0,
          variants: duplicateItem ? sourceData.variants : this.buildAllVariantsPayload(),
        };

        // If duplicating, always create a new record (not update)
        if (this.editMode && !duplicateItem) {
          // Update existing interest rate
          await new Promise((resolve, reject) => {
            this.$call.UpdateInterestRate(this.formData.interestRateID, requestData,
              (response) => {
                this.$swal({
                  title: 'Success!',
                  text: 'Interest rate updated successfully',
                  icon: 'success',
                  confirmButtonColor: '#4b2667'
                });
                this.loadInterestRates(); // Reload data
                this.closeDialog();
                this.loadingDialog = false;
                resolve(response);
              },
              (error) => {
                console.error('Error updating interest rate:', error);
                const errorMsg = error.response?.data?.message || error.response?.data?.errors?.[0] || 'Failed to update interest rate';
                this.$swal({
                  title: 'Error!',
                  text: errorMsg,
                  icon: 'error',
                  confirmButtonColor: '#d33'
                });
                this.loadingDialog = false;
                reject(error);
              }
            );
          });
        } else {
          // Create new interest rate
          await new Promise((resolve, reject) => {
            this.$call.AddInterestRate(requestData,
              (response) => {
                this.$swal({
                  title: 'Success!',
                  text: 'Interest rate created successfully',
                  icon: 'success',
                  confirmButtonColor: '#4b2667'
                });
                this.loadInterestRates(); // Reload data
                this.closeDialog();
                this.loadingDialog = false;
                this.duplicate_dialog_close();
                resolve(response);
              },
              (error) => {
                console.error('Error creating interest rate:', error);
                const errorMsg = error.response?.data?.message || error.response?.data?.errors?.[0] || 'Failed to create interest rate';
                this.$swal({
                  title: 'Error!',
                  text: errorMsg,
                  icon: 'error',
                  confirmButtonColor: '#d33'
                });
                this.loadingDialog = false;
                reject(error);
              }
            );
          });
        }
      } catch (error) {
        console.error('Error in saveAccountInfo:', error);
        this.loadingDialog = false;
      } finally {
        this.saving = false;
      }
    },

    SaveDuplicateInterest() {
      let self = this;
      if(!self.newDuplicateInterestRateName) {
        self.$swal({
          title: 'Warning!',
          text: 'Fill in Interest Rate Name',
          icon: 'warning',
        });
        return;
      }

      self.duplicateItem.interestRateName = self.newDuplicateInterestRateName;
      self.saveAccountInfo(self.duplicateItem);
    },

    confirmDelete(item) {
      this.$swal({
        title: 'Are you sure?',
        text: `Do you want to delete "${item.interestRateName}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteInterestRate(item);
        }
      });
    },

    async deleteInterestRate(item) {
      this.loadingDialog = true;
      
      try {
        await new Promise((resolve, reject) => {
          this.$call.DeleteInterestRate(item.interestRateID,
            (response) => {
              this.$swal({
                title: 'Deleted!',
                text: 'Interest rate deleted successfully',
                icon: 'success',
                confirmButtonColor: '#4b2667'
              });
              this.loadInterestRates(); // Reload data
              resolve(response);
            },
            (error) => {
              console.error('Error deleting interest rate:', error);
              this.$swal({
                title: 'Error!',
                text: 'Failed to delete interest rate',
                icon: 'error',
                confirmButtonColor: '#d33'
              });
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error('Error in deleteInterestRate:', error);
      } finally {
        this.loadingDialog = false;
      }
    },

    // Personal Loan Table Methods
    addProfession() {
      // const monthsCount = this.formData.personalLoanMonths.length;
      this.formData.personalLoanData.push({
        occupationId: null,
        salaryRanges: [
          {
            rangeMin: null,
            rangeMax: null,
            interestRates: [],
          },
        ],
      });
    },

    removeProfession(professionIndex) {
      if (this.formData.personalLoanData.length > 1) {
        this.formData.personalLoanData.splice(professionIndex, 1);
      }
    },

    addSalaryRange(professionIndex) {
      const monthsCount = this.formData.personalLoanMonths.length;
      this.formData.personalLoanData[professionIndex].salaryRanges.push({
        rangeMin: null,
        rangeMax: null,
        interestRates: [],
      });
    },

    removeSalaryRange(professionIndex, rangeIndex) {
      if (this.formData.personalLoanData[professionIndex].salaryRanges.length > 1) {
        this.formData.personalLoanData[professionIndex].salaryRanges.splice(rangeIndex, 1);
      }
    },

    addPersonalLoanMonth() {
      // Find the maximum month value and add 12 to it
      const maxMonth = this.formData.personalLoanMonths.reduce((max, month) => {
        const val = Number(month.value);
        return !isNaN(val) && val > max ? val : max;
      }, 0);
      
      const nextMonth = maxMonth > 0 ? maxMonth + 12 : 12;
      this.formData.personalLoanMonths.push({ value: nextMonth });
      
      // Add a new interest rate slot to all existing salary ranges
      // this.formData.personalLoanData.forEach(profession => {
      //   profession.salaryRanges.forEach(range => {
      //     range.interestRates.push(0);
      //   });
      // });
    },

    removePersonalLoanMonth(monthIndex) {
      if (this.formData.personalLoanMonths.length > 1) {
        this.formData.personalLoanMonths.splice(monthIndex, 1);
        
        // Remove the corresponding interest rate from all salary ranges
        this.formData.personalLoanData.forEach(profession => {
          profession.salaryRanges.forEach(range => {
            range.interestRates.splice(monthIndex, 1);
          });
        });
      }
    },

    validateNumericKeypress(event) {
      // Allow only digits 0-9
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
    },

    filterNumericInput(event, month) {
      // Filter out any non-numeric characters
      const value = event.target.value;
      const filtered = value.replace(/[^0-9]/g, '');
      
      // Update the month value with filtered result
      month.value = filtered;
      
      // Force update the input field
      this.$nextTick(() => {
        event.target.value = filtered;
      });
    },
  },
};
</script>

<style scoped>
.card2 {
  border-radius: 10px !important;
  margin-bottom: 2rem;
  padding-top: 1rem;
}

.btn-layout {
  padding: 14px 24px;
  width: 171px;
  height: 57px;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;
  color: #fff !important;
}

.btn-save {
  color: #fff !important;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 24px;
  width: 140px;
  height: 45px;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;
  flex: none;
  order: 1;
  flex-grow: 0;
}

.btn-close {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 10px;
  width: 135px;
  height: 45px;
  border: 1px solid #b4b4b4;
  border-radius: 18px;
  flex: none;
  order: 0;
  flex-grow: 0;
}

.search-field {
  border-radius: 30px !important;
  z-index: 9999 !important;
}

.search-field .v-input__control .v-input__slot {
  min-height: 48px !important;
  background: #f5f5f5 !important;
  box-shadow: none !important;
  border: 1px solid #e0e0e0 !important;
}

.search-field .v-input__prepend-inner {
  margin-right: 8px;
  margin-left: 4px;
}

.search-field .v-icon {
  color: #666;
}

:deep(.v-table__wrapper thead tr) {
  background-color: #f5f5f5 !important;
}

:deep(.v-table__wrapper thead th) {
  background-color: #f5f5f5 !important;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  height: 48px;
}

:deep(.v-data-table-server .v-table thead th) {
  background-color: #f5f5f5 !important;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  height: 48px;
}

:deep(.v-data-table-server .v-table thead tr th) {
  background-color: #f5f5f5 !important;
}

:deep(.v-data-table) {
  border: 1px solid #e1e1e1;
  border-radius: 24px;
  overflow: hidden;
}

:deep(.v-table) {
  border-radius: 24px;
  overflow: hidden;
}

.v-data-table {
  overflow-x: auto;
}

.table-container {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
}

.sticky-action {
  position: sticky !important;
  right: 0px !important;
  background-color: #fff !important;
  z-index: 10 !important;
}

:deep(.my-data-table thead th.sticky-action) {
  position: sticky !important;
  right: 0px !important;
  background-color: #f5f5f5 !important;
  z-index: 15 !important;
}

:deep(.my-data-table .v-table__wrapper) {
  overflow-x: auto !important;
}

:deep(.my-data-table table) {
  min-width: 1000px;
}

:deep(.v-table .v-table__wrapper > table > tbody > tr > td.sticky-action),
:deep(.v-table .v-table__wrapper > table > thead > tr > th.sticky-action) {
  position: sticky !important;
  right: 0px !important;
  z-index: 10 !important;
}

/* Dialog Styles */
.add-proactive-dialog {
  border-radius: 16px !important;
}

.add-proactive-dialog .v-card-title {
  padding-bottom: 5px !important;
}

.dialog-header {
  padding: 20px 24px !important;
}

.dialog-title {
  font-size: 1.25rem !important;
  color: #1f1f1f;
}

.dialog-subtitle {
  color: #898989;
}

.icon-background {
  padding: 8px;
  gap: 10px;
  width: 46px;
  height: 46px;
  background: #dee7f3;
  backdrop-filter: blur(2px);
  border-radius: 100px;
  box-shadow: none;
}

.dialog-header-add {
  background: #eeeff3;
  margin: 0 !important;
}

.header-icon-container {
  width: 48px;
  height: 48px;
  margin-right: 12px;
}

/* Action buttons group styling */
.action-buttons-group {
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
}

.action-btn {
  min-width: 36px !important;
  width: 36px;
  height: 36px;
  border-radius: 6px;
}

.action-btn .v-icon {
  font-size: 14px;
}

/* Category Management Styling */
.btn-category {
  padding: 14px 24px;
  width: 171px;
  height: 57px;
  background: linear-gradient(90deg, #2196f3 0%, #1976d2 100%);
  border-radius: 16px;
  color: #fff !important;
  margin-left: 8px;
}

.btn-category:hover {
  background: linear-gradient(90deg, #1976d2 0%, #1565c0 100%);
  opacity: 0.9;
}

/* Duplicate Button Styling */
.btn-duplicate {
  height: 36px;
  max-width: 36px;
  min-width: 36px;
  padding: 0 8px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: #fff !important;
  border-radius: 8px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3) !important;
}

.btn-duplicate:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4) !important;
  transform: translateY(-1px);
}

@media (max-width: 960px) and (min-width: 769px) {
  /* Tablet styles */
  .btn-layout,
  .btn-category {
    min-width: 120px;
  }
}

@media (max-width: 768px) {
  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .btn-layout,
  .btn-category {
    width: 100%;
    margin-left: 0;
    margin-right: 0 !important;
  }

  /* Ensure filter buttons are full width on mobile */
  .v-col:last-child .d-flex {
    justify-content: center !important;
  }

  .action-buttons-cell .action-btn {
    margin: 2px;
  }
}

/* Toggle Switch Styling */
::v-deep.toggle-switch.v-switch--inset .v-switch__thumb {
  height: 26px;
  width: 28px;
  transform: none !important;
}

::v-deep.toggle-switch.v-switch--inset .v-switch__track {
  width: 55px !important;
  height: 30px !important;
  background-color: #c2c2c2 !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  border: none !important;
  border-radius: 50px !important;
  min-width: none;
}

::v-deep.toggle-switch.v-switch--inset .v-selection-control--dirty .v-switch__thumb {
  background-color: #4e3982 !important;
}

::v-deep.toggle-switch.v-switch--inset .v-selection-control--dirty .v-switch__track {
  background-color: #ffffff !important;
  border: 1px solid #4e3982 !important;
}

/* Interest Rate Table Dropdown Styling */
.interest-rate-table :deep(.interest-type-dropdown .v-field) {
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%) !important;
  border-radius: 8px !important;
  color: white !important;
}

.interest-rate-table :deep(.interest-type-dropdown .v-field__input) {
  color: white !important;
  font-weight: 500 !important;
  padding: 8px 12px !important;
  min-height: 36px !important;
}

.interest-rate-table :deep(.interest-type-dropdown .v-field__append-inner) {
  color: white !important;
}

.interest-rate-table :deep(.interest-type-dropdown .v-select__selection-text) {
  color: white !important;
}

.interest-rate-table :deep(.interest-type-dropdown .v-field--variant-solo) {
  box-shadow: none !important;
}

.interest-rate-table :deep(.v-table) {
  border-collapse: collapse !important;
}

.interest-rate-table :deep(.v-table th),
.interest-rate-table :deep(.v-table td) {
  border: 1px solid #e0e0e0 !important;
}

/* Rounded Input Fields */
.rounded-input :deep(.v-field) {
  border-radius: 12px !important;
}

.rounded-input :deep(.v-field__outline) {
  border-radius: 12px !important;
}

.rounded-input :deep(.v-field__field) {
  border-radius: 12px !important;
}
::v-deep.toggle-switch.v-switch--inset .v-switch__thumb {
  height: 26px;
  width: 28px;
  transform: none !important;
}

/* Custom inset switch styling */
::v-deep.toggle-switch.v-switch--inset .v-switch__track {
  width: 55px !important;
  height: 30px !important;
  background-color: #c2c2c2 !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  border: none !important;
  border-radius: 50px !important;
  min-width: none;
}
::v-deep.toggle-switch.v-switch--inset .v-selection-control--dirty .v-switch__thumb {
  background-color: #4e3982 !important;
}
::v-deep.toggle-switch.v-switch--inset .v-selection-control--dirty .v-switch__track {
  background-color: #ffffff !important;
  border: 1px solid #4e3982 !important;
}

/* Personal Loan Table Styling */
.personal-loan-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  position: relative;
}

.personal-loan-table-wrapper {
  display: inline-block;
  min-width: 100%;
}

.personal-loan-table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  table-layout: auto;
}

.personal-loan-table thead tr {
  background-color: #fafafa;
}

.personal-loan-table th,
.personal-loan-table td {
  border: 1px solid #e0e0e0;
  padding: 12px 16px;
  white-space: nowrap;
  vertical-align: middle;
}

.personal-loan-table th {
  font-weight: 600;
  color: #666;
  position: sticky;
  top: 0;
  background-color: #fafafa;
  z-index: 10;
}

.personal-loan-table tbody tr:hover {
  background-color: #f5f5f5;
}

.personal-loan-table :deep(.v-text-field .v-field) {
  font-size: 14px;
}

.personal-loan-table :deep(.v-combobox .v-field) {
  font-size: 14px;
}

/* Scrollbar styling for better visibility */
.personal-loan-scroll-container::-webkit-scrollbar {
  height: 10px;
}

.personal-loan-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 5px;
  margin: 0 10px;
}

.personal-loan-scroll-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 5px;
}

.personal-loan-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Hide number input spinner arrows */
.personal-loan-table :deep(input[type="number"]::-webkit-outer-spin-button),
.personal-loan-table :deep(input[type="number"]::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

.personal-loan-table :deep(input[type="number"]) {
  -moz-appearance: textfield;
  appearance: textfield;
}

/* Loading Dialog Styles */
.loading-card {
  background: white;
  border-radius: 16px !important;
  padding: 20px;
  min-width: 400px;
}

.loading-title {
  color: #1976d2;
  font-weight: 600;
  margin-bottom: 8px;
}

.loading-subtitle {
  color: #757575;
  font-size: 0.9rem;
  margin: 0;
}

/* Modern Variant Panel Styles */
.variant-panels {
  gap: 16px;
}

.variant-panel {
  border: none !important;
  border-radius: 16px !important;
  overflow: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.variant-panel:hover {
  box-shadow: 0 8px 24px rgba(79, 57, 130, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}

.variant-panel-title {
  background: linear-gradient(135deg, #fafbfd 0%, #f0f2f8 100%) !important;
  padding: 16px 20px !important;
  min-height: 72px !important;
  border-bottom: 1px solid rgba(79, 57, 130, 0.08);
}

.variant-panel-title:hover {
  background: linear-gradient(135deg, #f5f7fb 0%, #ebeef6 100%) !important;
}

.variant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.variant-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.variant-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.variant-number {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #504897 0%, #6b5bb3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(79, 57, 130, 0.3);
  flex-shrink: 0;
}

.variant-number-text {
  color: white;
  font-weight: 700;
  font-size: 16px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.variant-input-wrapper {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 12px;
  padding: 8px 16px;
  border: 1px solid rgba(79, 57, 130, 0.15);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  max-width: 400px;
  flex: 1;
  transition: all 0.2s ease;
}

.variant-input-wrapper:hover {
  border-color: rgba(79, 57, 130, 0.3);
  box-shadow: 0 2px 8px rgba(79, 57, 130, 0.1);
}

.variant-input-wrapper:focus-within {
  border-color: #504897;
  box-shadow: 0 0 0 3px rgba(79, 57, 130, 0.15);
}

.variant-input-icon {
  color: #504897;
  opacity: 0.7;
  margin-right: 8px;
}

.variant-name-input {
  flex: 1;
}

.variant-name-input :deep(.v-field__input) {
  padding: 0 !important;
  min-height: 32px !important;
  font-weight: 500;
  color: #333;
  font-size: 15px;
}

.variant-name-input :deep(.v-field) {
  padding: 0 !important;
}

.default-calc-label {
  font-size: 12px;
  font-weight: 500;
  color: #666;
  white-space: nowrap;
  margin-right: 4px;
}

.installment-type-select {
  min-width: 200px;
}

.installment-type-select :deep(.v-field__input) {
  padding: 0 8px !important;
  min-height: 32px !important;
  font-weight: 600;
  color: #1976d2;
  font-size: 13px;
}

.installment-type-select :deep(.v-field) {
  padding: 0 !important;
}

.variant-status-chip {
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 0 12px;
  height: 28px;
}

.variant-delete-btn {
  border-radius: 10px !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  padding: 0 12px !important;
  height: 36px !important;
}

.variant-delete-btn:hover {
  background: rgba(244, 67, 54, 0.15) !important;
}

/* Expansion panel arrow */
.variant-panel :deep(.v-expansion-panel-title__overlay) {
  background: transparent !important;
}

.variant-panel :deep(.v-expansion-panel-title__icon) {
  color: #504897;
  margin-left: 8px;
}

/* duplicate dialog */
.dialog-header-doc{
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 10px 20px;
  border-bottom: 1px solid #e1e5e9;
  position: relative;
}

.delete-cancel-btn-1 {
  background-color: white !important;
  color: #C30000 !important;
  border: 1px solid #C30000 !important;
  border-radius: 11px !important;
  padding: 10px 24px !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  height: 40px !important;
  min-width: 90px !important;
}
.delete-cancel-btn-1:hover {
  transform: scale(1.1);
}
.modern-input :deep(.v-field) {
  border-radius: 16px;
  transition: all 0.2s ease;
  margin-top: 10px;
}
.save-btn-edit {
  background: linear-gradient(135deg, #C32E0E 0%, #F67760 100%);
  color: white !important;
}
.save-btn-edit:hover {
  background: linear-gradient(90deg, #F36243 0%, #FF8E7A 100%) !important;
  color: white !important;
}

.modern-table :deep(.v-table__wrapper) {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar) {
  width: 6px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-track) {
  background: rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb) {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb:hover) {
  background: rgba(25, 118, 210, 0.5);
}
</style>
